import WithConfirmationModal from '@zen/Components/WithConfirmationModal';
import { IconButton, Pill } from '@zen/DesignSystem';
import type { TableColumn } from '@zen/DesignSystem/types';

import type { Webhook } from './types';

interface TableParams {
  accountHasBusinessUnits: boolean;
  canManage: boolean;
  onDelete: (id: string) => void;
  onEdit: (webhook: Webhook) => void;
}

export const getColumns = ({ accountHasBusinessUnits, canManage, onEdit, onDelete }: TableParams): TableColumn<Webhook>[] => {
  const nameColumn: TableColumn<Webhook> = {
    key: 'name',
    title: 'Name',
    sortable: false,
    width: 300
  };

  const callbackUrlColumn: TableColumn<Webhook> = {
    key: 'callbackUrl',
    title: 'Callback URL',
    render: (_, { callbackUrl }: Webhook) => <div className="font-mono text-xs">{callbackUrl}</div>,
    sortable: false
  };

  const statusColumn: TableColumn<Webhook> = {
    key: 'status',
    title: 'Status',
    render: (_, { isDisabled }: Webhook) => (
      <Pill type={isDisabled ? 'problem' : 'success'}>{isDisabled ? 'Disabled' : 'Enabled'}</Pill>
    ),
    sortable: false
  };

  const businessUnitColumn: TableColumn<Webhook> = {
    key: 'businessUnit',
    title: 'Business unit',
    render: (_, { account }: Webhook) => account?.tradingName,
    sortable: false
  };

  const actionsColumn: TableColumn<Webhook> = {
    key: 'actions',
    sortable: false,
    fixed: 'right',
    render: (_, webhook) => {
      return (
        <div className="flex space-x-2">
          <IconButton icon="zicon-edit" onClick={() => onEdit(webhook)} size="medium" title="Edit" variant="ghost" />
          <WithConfirmationModal
            confirmLabel="Delete"
            header="Delete Webhook?"
            message="Removing this means you will no longer be notified about the topics you subscribed to. This action cannot be undone."
            onConfirm={() => onDelete(webhook.id)}
          >
            {({ requestConfirm }) => (
              <IconButton icon="zicon-trash" onClick={requestConfirm} size="medium" title="Delete" variant="ghost" />
            )}
          </WithConfirmationModal>
        </div>
      );
    }
  };

  return [
    nameColumn,
    callbackUrlColumn,
    ...(accountHasBusinessUnits ? [businessUnitColumn] : []),
    statusColumn,
    ...(canManage ? [actionsColumn] : [])
  ];
};
